import React from 'react';
import { useTranslation } from 'react-i18next';
import { MusicalNoteIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { BaseTrackItemProps } from './BaseTrackItem';

export default function AudioTrackItem({ clip, pixelsPerSecond, isSelected, onSelect, onToggleVolume }: BaseTrackItemProps) {
  const { t } = useTranslation('editor');
  const clipWidth = clip.duration * pixelsPerSecond;
  const currentVolume = clip.editInfo?.dialogueVolume ?? 1;
  const isMuted = currentVolume === 0;

  const getBackgroundColor = () => {
    if (clip.hasAudio && isMuted) return 'bg-amber-500/45';
    if (clip.hasAudio) return 'bg-blue-500/50';
    if (clip.isGenerating) return 'bg-gray-400/50';
    return 'bg-gray-400/30';
  };

  const getPattern = () => {
    if (!clip.hasAudio && clip.isGenerating) {
      return 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)';
    }
    return 'none';
  };

  const getSelectionStyle = () => {
    if (isSelected) return 'ring-2 ring-blue-400 bg-blue-200/30 shadow-lg';
    return '';
  };

  const handleVolumeToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!clip.hasAudio || !onToggleVolume) return;

    onToggleVolume(clip.videoClip?.id || clip.id.replace(/^audio-/, ''), isMuted ? 1 : 0);
  };

  return (
    <div
      className={`absolute top-1 bottom-1 rounded cursor-pointer hover:opacity-80 transition-all overflow-hidden ${getBackgroundColor()
        } ${getSelectionStyle()}`}
      style={{
        left: `${clip.startTime * pixelsPerSecond}px`,
        width: `${clipWidth}px`,
        backgroundImage: getPattern()
      }}
      onClick={onSelect}
    >
      {clip.hasAudio && onToggleVolume && (
        <button
          onClick={handleVolumeToggle}
          className="absolute left-1 top-1/2 -translate-y-1/2 w-4 h-4 bg-black/60 rounded flex items-center justify-center z-10 cursor-pointer transition-colors hover:bg-black/80"
          title={isMuted ? t('timeline.audioTrackItem.enableSound') : t('timeline.audioTrackItem.disableSound')}
          aria-label={isMuted ? t('timeline.audioTrackItem.enableSound') : t('timeline.audioTrackItem.disableSound')}
        >
          {isMuted ? (
            <BellSlashIcon className="w-3 h-3 text-red-400" />
          ) : (
            <MusicalNoteIcon className="w-3 h-3 text-green-400" />
          )}
        </button>
      )}

      {clip.hasAudio ? (
        <div className={`absolute inset-0 flex items-center justify-around px-1 ${isMuted ? 'opacity-35' : ''}`}>
          {Array.from({ length: Math.max(Math.floor(clipWidth / 4), 10) }, (_, i) => {
            const height = 20 + Math.sin(i * 0.5) * 15 + Math.cos(i * 0.3) * 10;
            return (
              <div
                key={i}
                className="bg-white rounded-sm"
                style={{
                  width: '2px',
                  height: `${height}%`,
                }}
              />
            );
          })}
        </div>
      ) : clip.isGenerating ? (
        <div className="p-1 h-full flex items-center justify-center">
          <span className="text-xs text-gray-600 truncate">加载中...</span>
        </div>
      ) : (
        <div className="p-1 h-full flex items-center justify-center">
          <span className="text-xs text-gray-600 truncate">无音频</span>
        </div>
      )}
    </div>
  );
}
